// Load environment variables
const getEnvVar = (key: string, fallback: string): string => {
  return import.meta.env[key] || fallback;
};

export const CONTACT_INFO = {
  phone: getEnvVar('VITE_CONTACT_PHONE', ''),
  email: getEnvVar('VITE_CONTACT_EMAIL', ''),
  address: getEnvVar('VITE_CONTACT_ADDRESS', ''),
};

export const SOCIAL_LINKS = {
  facebook: getEnvVar('VITE_FACEBOOK_URL', ''),
  instagram: getEnvVar('VITE_INSTAGRAM_URL', ''),
};

export const GOOGLE_CALENDAR_EMBED_URL = getEnvVar(
  'VITE_GOOGLE_CALENDAR_EMBED_URL',
  ''
);

export const GOOGLE_CALENDAR_ID = getEnvVar(
  'VITE_GOOGLE_CALENDAR_ID',
  ''
);

export const GOOGLE_CALENDAR_API_KEY = getEnvVar(
  'VITE_GOOGLE_CALENDAR_API_KEY',
  ''
);

export const GOOGLE_CALENDAR_ICAL_URL = getEnvVar(
  'VITE_GOOGLE_CALENDAR_ICAL_URL',
  ''
);

// Cal.com embed URL - uses embed parameter for iframe embedding with light theme
const getCalComEmbedUrl = (): string => {
  const url = getEnvVar('VITE_CALCOM_EMBED_URL', '');
  if (!url) return '';
  
  // Build URL with embed and theme parameters
  const separator = url.includes('?') ? '&' : '?';
  let embedUrl = url;
  
  // Add embed parameter if not present
  if (!embedUrl.includes('embed=')) {
    embedUrl = `${embedUrl}${separator}embed=true`;
  }
  
  // Force light theme to remove black space
  const themeSeparator = embedUrl.includes('?') ? '&' : '?';
  if (!embedUrl.includes('theme=')) {
    embedUrl = `${embedUrl}${themeSeparator}theme=light`;
  }
  const phoneSeparator = embedUrl.includes('?') ? '&' : '?';
  if (!embedUrl.includes('phoneCountryCode=')) {
    embedUrl = `${embedUrl}${phoneSeparator}phoneCountryCode=US`;
  }
  
  return embedUrl;
};

export const CALCOM_EMBED_URL = getCalComEmbedUrl();

// Get the base Cal.com URL without embed parameters (for "Open in new window" link)
export const CALCOM_BASE_URL = ((): string => {
  const url = getEnvVar('VITE_CALCOM_EMBED_URL', '');
  if (!url) return '';
  
  // Remove any query parameters to get the base URL
  const baseUrl = url.split('?')[0];
  return baseUrl;
})();

// Cal.com trial class URL - can be set via env or defaults to /trial-class
export const CALCOM_TRIAL_CLASS_URL = ((): string => {
  const envUrl = getEnvVar('VITE_CALCOM_TRIAL_CLASS_URL', '');
  if (envUrl) return envUrl;
  
  // Default to trial-class if no env var is set
  const baseUrl = CALCOM_BASE_URL;
  if (!baseUrl) return '';
  return `${baseUrl}/trial-class`;
})();

export const CALCOM_API_KEY = getEnvVar(
  'VITE_CALCOM_API_KEY',
  ''
);

// Extract Cal.com username from embed URL (e.g., https://cal.com/delta-bjj -> delta-bjj)
export const CALCOM_USERNAME = (() => {
  const url = getEnvVar('VITE_CALCOM_EMBED_URL', '');
  if (!url) return '';
  const match = url.match(/cal\.com\/([^/?]+)/);
  return match ? match[1] : '';
})();

// Cal.com event slug (default to empty, can be set via env var)
export const CALCOM_EVENT_SLUG = getEnvVar('VITE_CALCOM_EVENT_SLUG', '');

// Cal.com OAuth Client ID (optional, for managed users)
export const CALCOM_CLIENT_ID = getEnvVar('VITE_CALCOM_CLIENT_ID', '');

export const ADULT_PROGRAMS = [
  {
    id: 1,
    title: 'BJJ Fundamentals',
    description: 'Perfect for beginners. Learn the core techniques and principles of Brazilian Jiu-Jitsu in a supportive, structured environment. Focused on strong foundations, movement, and positional understanding.',
    schedule: 'Mon & Wed: 6:00–7:00am, 11:00am–12:00pm | Thu: 11:00am–12:00pm | Sun: 9:00–10:00am | Tue: 7:30–8:30pm | Mon & Wed: 6:10–7:00pm (BJJ Drills for Beginners)',
    image: '/images/cover-images/bjj-fundamentals.jpeg',
    label: 'ALL LEVELS',
    details: 'Strictly focusing on the basics of Brazilian Jiu-Jitsu mechanics and self-defense.',
    alt: 'Adult students practicing BJJ fundamentals techniques at Delta BJJ',
  },
  {
    id: 2,
    title: 'BJJ All Levels',
    description: 'Open to all skill levels. Refine your technique, improve conditioning, and spar with practitioners of varying experience in a dynamic, challenging environment.',
    schedule: 'Mon & Wed: 7:00–8:00pm | Sat: 11:00am–12:00pm',
    image: '/images/cover-images/bjj-all-levels.jpeg',
    label: 'MIXED LEVELS',
    details: 'A mix of basic and advanced techniques where beginners are accompanied by higher belts.',
    alt: 'Mixed level BJJ students training together at Delta BJJ',
  },
  {
    id: 3,
    title: 'No-Gi BJJ',
    description: 'Train without the gi. Faster-paced sessions focusing on modern submission grappling.',
    schedule: 'Tue 11am-12pm | Thu 6:00-7:00am | Fri 11am-12pm, 6:00-7:00pm',
    image: '/images/cover-images/no-gi-bjj.jpeg',
    label: 'NO-GI',
    details: 'Techniques without the traditional uniform, focusing on overhooks, underhooks, and clinch work.',
    alt: 'No-Gi Brazilian Jiu-Jitsu training session at Delta BJJ',
  },
  {
    id: 4,
    title: 'BJJ Drills for Beginners',
    description: 'Focus on fundamental movements and drilling techniques to build a strong foundation. NO sparring.',
    schedule: 'Mon/Wed 6:10-7:00pm',
    image: '/images/cover-images/bjj-drills-for-beginners.jpeg',
    label: 'BEGINNERS',
    details: 'A dedicated class for those just starting their journey, focusing on repetitive movements to build muscle memory.',
    alt: 'Beginner BJJ students drilling fundamental techniques at Delta BJJ',
  },
  {
    id: 5,
    title: 'Competition Training',
    description: 'High-intensity sessions for active competitors or those looking to push their limits.',
    schedule: 'Check our schedule for specific sessions',
    image: '/images/cover-images/competition-training.jpeg',
    label: 'ADVANCED',
    details: 'Focused on tournament preparation, strategy, and high-level sparring.',
    alt: 'Advanced competition training session at Delta BJJ',
  },
];

export const YOUTH_PROGRAMS = [
  {
    id: 1,
    title: 'Juniors 1 BJJ',
    description: 'Ages 5-8. Introduction to BJJ through fun games and basic techniques.',
    schedule: 'Mon/Wed 4:15 PM',
    image: '/images/cover-images/juniors1bjj.jpeg',
    label: 'AGES 5-8',
    details: 'Developing motor skills, discipline, and basic self-defense through Brazilian Jiu-Jitsu.',
    alt: 'Kids BJJ class drilling guard passes at Delta BJJ',
  },
  {
    id: 2,
    title: 'Juniors 2 BJJ',
    description: 'Ages 9-12. Building on fundamentals with more structured training.',
    schedule: 'Tue/Thu 4:00 PM',
    image: '/images/cover-images/juniors2bjj.jpeg',
    label: 'AGES 9-12',
    details: 'More technical focus while maintaining a fun and supportive environment.',
    alt: 'Youth BJJ students practicing techniques at Delta BJJ',
  },
  {
    id: 3,
    title: 'Teen BJJ',
    description: 'Ages 13-17. Training for teenagers preparing for adult classes.',
    schedule: 'Mon/Wed/Fri 5:00 PM',
    image: '/images/cover-images/teenbjj.jpeg',
    label: 'AGES 13-17',
    details: 'Preparing young adults for the technical and physical demands of adult training.',
    alt: 'Teen BJJ class training at Delta BJJ',
  },
];

export const INSTRUCTORS = [
  {
    id: 1,
    name: 'Professor Gerson Sanginitto',
    title: 'Head Instructor',
    rank: 'Black Belt 6th Degree',
    quote: 'The mat is an honest place. It teaches patience, resilience, and awareness. My goal at Delta BJJ is to help every student grow with confidence, humility, solid technique, and to become a better version of themselves.',
    bio: 'Black belt 6th degree since 1998, and Judo black belt since 1990.',
    achievements: [
      { title: 'IBJJF World Master Champion', year: '2023' },
      { title: '3× Champion — IBJJF Master International', location: 'North America' },
      { title: '3× Champion — IBJJF Los Angeles International Open' },
      { title: '3× Champion — IBJJF Orange County International Open' },
    ],
    image: '/images/instructors/gerson_sanginitto.jpeg',
    alt: 'Professor Gerson Sanginitto, Head Brazilian Jiu-Jitsu Instructor',
  },
  {
    id: 2,
    name: 'Professor Marcelo Casalino',
    title: 'Instructor',
    rank: 'Black Belt 4th Degree',
    quote: '',
    bio: '',
    achievements: [],
    image: '/images/instructors/professor_marcelo_casalino.jpeg',
    alt: 'Professor Marcelo Casalino, Brazilian Jiu-Jitsu Instructor',
  },
  {
    id: 3,
    name: 'Professor Romualdo Nakvasas',
    title: 'Instructor',
    rank: 'Black Belt 3rd Degree',
    quote: '',
    bio: '',
    achievements: [],
    image: '/images/instructors/professor_romualdo_nakvasas.jpeg',
    alt: 'Professor Romualdo Nakvasas, Brazilian Jiu-Jitsu Instructor',
  },
  {
    id: 4,
    name: 'Professor André Ramos',
    title: 'Instructor',
    rank: 'Black Belt 3rd Degree',
    quote: '',
    bio: '',
    achievements: [],
    image: '/images/instructors/professor_andre_ramos.jpeg',
    alt: 'Professor Andre Ramos, Brazilian Jiu-Jitsu Instructor',
  },
  {
    id: 6,
    name: 'Coach Marvin Amaya',
    title: 'Coach',
    rank: 'Brown Belt',
    quote: '',
    bio: '',
    achievements: [],
    image: '/images/instructors/coach_marvin_amaya.jpeg',
    alt: 'Coach Marvin Amaya, Brazilian Jiu-Jitsu Coach',
  },
  {
    id: 7,
    name: 'Assistant Coach Sophia Sanginitto',
    title: 'Assistant Coach',
    rank: 'Yellow Belt',
    quote: '',
    bio: '',
    achievements: [],
    image: '/images/instructors/assistant_coach_sophia_sanginitto.jpeg',
    alt: 'Assistant Coach Sophia Sanginitto, Brazilian Jiu-Jitsu Assistant Coach',
  },
  {
    id: 8,
    name: 'Michael Briggs',
    title: 'Coach',
     quote: '',
     rank: 'Brown Belt',
    bio: '',
    achievements: [],
    image: '/images/instructors/coach_michael_briggs.jpeg',
    alt: 'Michael Briggs, Brazilian Jiu-Jitsu Coach',
  },
  {
    id: 9,
    name: 'Sensei Andre Hengchua',
    title: 'Sensei',
    rank: 'Judo Black Belt',
    quote: '',
    bio: 'Judo Black Belt since 2022.',
    achievements: [],
    image: '/images/instructors/sensei_andre_hengchua.jpeg',
    alt: 'Sensei Andre Hengchua, Judo Instructor',
  },
];

